import React, { useCallback, useMemo, useState } from "react";
import { styled, css } from "styled-components";
import { Field, useFormikContext } from "formik";
import capitalize from "lodash/capitalize";
import Select from "components/common/Select";
import Popover from "components/common/Popover";
import { HoverForHelp, ModalButtonToolbar } from "components/common";
import { Alert, Button, Input } from "components/bootstrap";
import { mappedUnitsFromJSON as units } from "views/components/visualizations/utils/unitConverters";
import getUnitTextLabel from "views/components/visualizations/utils/getUnitTextLabel";
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;

  & .control-label {
    font-weight: normal;
  }

  width: 300px;
`;
const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  height: 25px;
`;
export const StyledButton = styled(Button)(
  ({ theme }) => css`
    background-color: ${theme.colors.gray[60]};
    padding: 1px 2px;
    min-width: 20px;
    border-radius: 3px;
    color: ${theme.colors.variant.lightest.default};

    &:hover {
      background-color: ${theme.colors.gray[80]};
      color: ${theme.colors.variant.lightest.default};
    }
  `
);
const FieldUnitPopover = ({ field, predefinedUnit }) => {
  const [show, setShow] = useState(false);
  const { setFieldValue, values } = useFormikContext();
  const currentUnitType = useMemo(() => {
    var _a, _b;
    return (_b = (_a = values == null ? void 0 : values.units) == null ? void 0 : _a[field]) == null ? void 0 : _b.unitType;
  }, [values, field]);
  const unitTypesOptions = useMemo(() => Object.keys(units).map((key) => ({ value: key, label: capitalize(key) })), []);
  const unitOptions = useMemo(
    () => currentUnitType && units[currentUnitType].map(({ abbrev, name }) => ({ value: abbrev, label: capitalize(name) })),
    [currentUnitType]
  );
  const toggleShow = () => setShow((cur) => !cur);
  const onUnitTypeChange = useCallback(
    (val) => {
      const fieldUnitSettings = val ? { unitType: val, abbrev: void 0 } : void 0;
      setFieldValue(`units.${field}`, fieldUnitSettings);
    },
    [field, setFieldValue]
  );
  const badgeLabel = useMemo(() => {
    var _a, _b;
    const curUnit = (_b = (_a = values == null ? void 0 : values.units) == null ? void 0 : _a[field]) == null ? void 0 : _b.abbrev;
    return getUnitTextLabel(curUnit) || "...";
  }, [field, values == null ? void 0 : values.units]);
  const predefinedInfo = useMemo(() => {
    if (!(predefinedUnit == null ? void 0 : predefinedUnit.isDefined)) return null;
    const unitName = units[predefinedUnit == null ? void 0 : predefinedUnit.unitType].find(({ abbrev }) => abbrev === (predefinedUnit == null ? void 0 : predefinedUnit.abbrev)).name;
    return /* @__PURE__ */ React.createElement(React.Fragment, null, "Unit ", /* @__PURE__ */ React.createElement("b", null, unitName), " was configured for field ", /* @__PURE__ */ React.createElement("b", null, field), ". Changing this unit might represent data incorrectly on the charts.");
  }, [field, predefinedUnit == null ? void 0 : predefinedUnit.abbrev, predefinedUnit == null ? void 0 : predefinedUnit.isDefined, predefinedUnit == null ? void 0 : predefinedUnit.unitType]);
  const onClear = useCallback(() => {
    setFieldValue(`units.${field}`, void 0);
    toggleShow();
  }, [field, setFieldValue]);
  return /* @__PURE__ */ React.createElement(Popover, { position: "right", opened: show, withArrow: true }, /* @__PURE__ */ React.createElement(Popover.Target, null, /* @__PURE__ */ React.createElement(ButtonContainer, null, /* @__PURE__ */ React.createElement(StyledButton, { bsSize: "xs", onClick: toggleShow, title: `${field} unit settings` }, badgeLabel))), /* @__PURE__ */ React.createElement(Popover.Dropdown, { title: `${field} unit settings` }, /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(Field, { name: `units.${field}.unitType` }, ({ field: { name, value }, meta: { error } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "metric-unit-type-field",
      label: "Type",
      error,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      Select,
      {
        id: "metric-unit-type-select",
        onChange: onUnitTypeChange,
        name,
        value,
        "aria-label": "Select a unit type",
        options: unitTypesOptions,
        size: "small"
      }
    )
  )), currentUnitType && /* @__PURE__ */ React.createElement(Field, { name: `units.${field}.abbrev` }, ({ field: { name, value, onChange }, meta: { error } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "metric-unit-field",
      label: /* @__PURE__ */ React.createElement("span", null, "Unit", " ", /* @__PURE__ */ React.createElement(HoverForHelp, { displayLeftMargin: true }, "Unit used to calculate the displayed value on charts.")),
      error,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      Select,
      {
        id: "metric-unit-select",
        onChange: (fieldName) => onChange({ target: { name, value: fieldName } }),
        name,
        value,
        "aria-label": "Select a unit",
        options: unitOptions,
        size: "small"
      }
    )
  )), predefinedInfo && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, predefinedInfo), /* @__PURE__ */ React.createElement(ModalButtonToolbar, null, /* @__PURE__ */ React.createElement(Button, { bsSize: "xs", onClick: onClear }, "Clear"), /* @__PURE__ */ React.createElement(Button, { bsSize: "xs", bsStyle: "primary", onClick: toggleShow }, "OK")))));
};
export default FieldUnitPopover;
